/**
 * RisoluzioneFields — F-004 Phase 3 (IMPL-P3-004).
 *
 * Field group per la variante RISOLUZIONE. `Controller` + `useFormContext`
 * sopra `AdempimentoDraft`.
 *
 * Campi:
 *  - dataRisoluzione: Input type="date" (bounded per BR-4 dalla validazione
 *    dello schema — la data non può precedere la stipula né superare la
 *    scadenza naturale; l'errore bloccante arriva da `applyAdempimentoRefinements`).
 *  - motivo: `RadioGroup`/`RadioGroupItem` con `role="radiogroup"` + gruppo
 *    etichettato via `<Label id>` + `aria-labelledby` (uno screen reader deve
 *    annunciare "Motivo, gruppo di pulsanti di scelta").
 *  - motivoAltro: appare condizionalmente quando `motivo === 'ALTRO'`;
 *    programmaticamente associato via `aria-controls`/`aria-expanded` così che
 *    gli utenti AT sappiano che è comparso un nuovo campo obbligatorio.
 *
 * Il valore `motivo` è un enum lato schema (`ACCORDO | RECESSO_INQUILINO |
 * ALTRO`) — nessuna duplicazione: le opzioni etichettate vivono qui solo come
 * mapping presentazionale value→label.
 */
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import type { AdempimentoDraft } from '@app/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

/** Normalizza un valore RHF (Date | stringa ISO | undefined) a `YYYY-MM-DD`. */
function toDateInputValue(raw: unknown): string {
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  if (typeof raw === 'string') return raw.slice(0, 10);
  return '';
}

const MOTIVO_OPTIONS: ReadonlyArray<{ value: 'ACCORDO' | 'RECESSO_INQUILINO' | 'ALTRO'; label: string }> = [
  { value: 'ACCORDO', label: 'Risoluzione per accordo tra le parti' },
  { value: 'RECESSO_INQUILINO', label: "Recesso dell'inquilino" },
  { value: 'ALTRO', label: 'Altro (specificare)' },
];

export function RisoluzioneFields() {
  const {
    control,
    formState: { errors },
  } = useFormContext<AdempimentoDraft>();

  const motivo = useWatch({ control, name: 'motivo' });
  const isAltro = motivo === 'ALTRO';

  const risErrors = errors as Record<string, { message?: string } | undefined>;

  return (
    <div className="space-y-4">
      {/* Data risoluzione */}
      <div className="space-y-1">
        <Label htmlFor="data-risoluzione">Data di risoluzione *</Label>
        <Controller
          name="dataRisoluzione"
          control={control}
          render={({ field }) => (
            <Input
              id="data-risoluzione"
              type="date"
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              value={toDateInputValue(field.value)}
              onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : undefined)}
              aria-invalid={!!risErrors.dataRisoluzione}
              aria-describedby={risErrors.dataRisoluzione ? 'data-risoluzione-error' : undefined}
            />
          )}
        />
        {risErrors.dataRisoluzione && (
          <p id="data-risoluzione-error" className="text-xs text-destructive">
            {risErrors.dataRisoluzione.message}
          </p>
        )}
      </div>

      {/* Motivo — radiogroup etichettato */}
      <div className="space-y-2">
        <Label id="risoluzione-motivo-label">Motivo *</Label>
        <Controller
          name="motivo"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value ?? ''}
              onValueChange={field.onChange}
              aria-labelledby="risoluzione-motivo-label"
              aria-invalid={!!risErrors.motivo}
              aria-controls="risoluzione-motivo-altro"
              aria-expanded={isAltro}
            >
              {MOTIVO_OPTIONS.map((opt) => {
                const id = `motivo-${opt.value.toLowerCase()}`;
                return (
                  <div key={opt.value} className="flex items-center gap-2">
                    <RadioGroupItem value={opt.value} id={id} />
                    <Label htmlFor={id} className="cursor-pointer font-normal">
                      {opt.label}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          )}
        />
        {risErrors.motivo && (
          <p className="text-xs text-destructive">{risErrors.motivo.message}</p>
        )}
      </div>

      {/* Motivo "Altro" — appare condizionalmente, programmaticamente associato */}
      {isAltro && (
        <div id="risoluzione-motivo-altro" className="space-y-1">
          <Label htmlFor="motivo-altro-input">Specifica il motivo *</Label>
          <Controller
            name="motivoAltro"
            control={control}
            render={({ field }) => (
              <Input
                id="motivo-altro-input"
                {...field}
                value={field.value ?? ''}
                aria-invalid={!!risErrors.motivoAltro}
                aria-describedby={risErrors.motivoAltro ? 'motivo-altro-error' : undefined}
              />
            )}
          />
          {risErrors.motivoAltro && (
            <p id="motivo-altro-error" className="text-xs text-destructive">
              {risErrors.motivoAltro.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
